import axios from "axios";
import { useAppContext } from "../../context/AuthContext";
import AuthorDto from "../../model/author/AuthorDto";
import ArticleDto from "../../model/manuscript/ArticleDto";
import Endpoints from "../../utils/Endpoints";
import { axiosConfig } from "../../utils/Utils";

interface ArticleControllerType {
  axiosGetAllArticles: any;
  axiosGetTypes: any;
  axiosArticleSave: any;
  axiosArticleUpdate: any;
  axiosGetKeywords: any;
  axiosAuthorSave: any;
  axiosGetMyArticle: any;
  axiosGetMyArticleWithStatus: any;
  axiosUploadFile: any;
  axiosGetArticleById: any;
  axiosGetBlacklistAuthorList: any;
  axiosArticleSearch: any;
  axiosGetReviewersStatistics: any;
  axiosGetEditorArticleList: any;
  axiosSetReviewersCount: any;
  axiosGetAddedReviewerList: any;
  axiosAddReviewer: any;
  axiosDeleteReviewer: any;
  axiosGetMailSuggestionToReviewer: any;
  axiosSendMailToAllReviewers: any;
  axiosSendMailToReviewer: any;
  axiosGetUploadUserList: any;
  axiosGetAssistantToDoList: Function;
  axiosPostArticleAddNote: Function;
  axiosPostArticleRegenerateCombinedPdf: Function;
  axiosPostArticleCorrection: Function;
  axiosPostAssistantDecide: Function;
  axiosPostAddFile: Function;
  axiosGetReviewerToDoList: any;
  axiosPostAcceptDeclineArticle: any;
  axiosGetArticleReviewers: any;
  axiosGetMailSuggestionToAssociate: any;
  axiosPostAssignAssociate: any;
  axiosPostDraftReview: any;
  axiosGetReview: any;
  axiosPostSubmitReview: any;
  axiosGetArticlesForAssociate: any;
  axiosGetAssociateMail: any;
  axiosPostReturnToAssistant: Function;
  axiosPostAssociateMail: any;
  axiosGetEditorMail: any;
  axiosPostEditorMail: any;
  axiosGetAssociateStatistics: any;
  axiosGetAuthorList: any;
  axiosGetArticleOverallPdf: any;
  axiosPostSuggestedReviewer: any;
  axiosRateReview: Function;
  axiosGetAssociateMyArticle: Function;
  axiosPostSubmitRevision: Function;
  axiosPostSubmitCorrection: Function;
  axiosGetArticleLog: Function;
  axiosGetArticleEmailLog: Function;
  axiosGetAssistantRejectMail: Function;
  axiosPostDeleteFile: Function;
  axiosGetAssistantGetCorrectionMailsList: Function;
  axiosPostAssignSameReviewers: Function;
  axiosPostDeleteArticle: Function;
  axiosPostDeleteFiles: Function;
}

export function ArticleController(): ArticleControllerType {
  const { authData } = useAppContext();

  const axiosGetAllArticles = () => {
    return axios.get(Endpoints.ARTICLE + "/all", axiosConfig(authData!.token));
  };

  const axiosGetAssociateMyArticle = () => {
    return axios.get(Endpoints.ARTICLE + "/associate/my-articles", axiosConfig(authData!.token));
  };

  const axiosGetTypes = (token: string) => {
    return axios.get(Endpoints.ARTICLE + "/types", axiosConfig(token ? token : authData!.token));
  };

  const axiosGetEditorArticleList = () => {
    return axios.get(Endpoints.ARTICLE + "/editor/article-list", axiosConfig(authData!.token));
  };

  const axiosGetArticleLog = (id: number) => {
    return axios.get(`${Endpoints.ARTICLE}/${id}/log`, axiosConfig(authData!.token));
  };

  const axiosGetArticleEmailLog = (id: number) => {
    return axios.get(`${Endpoints.ARTICLE}/${id}/mail-log`, axiosConfig(authData!.token));
  };

  const axiosArticleSave = (article: ArticleDto) => {
    return article.id
      ? axiosArticleUpdate(article)
      : axios.post(Endpoints.ARTICLE + "/save", article, axiosConfig(authData!.token));
  };

  const axiosArticleUpdate = (article: ArticleDto) => {
    return axios.post(Endpoints.ARTICLE + "/" + article.id + "/update", article, axiosConfig(authData!.token));
  };

  const axiosGetKeywords = () => {
    return axios.get(Endpoints.ARTICLE + "/keywords", axiosConfig(authData!.token));
  };

  const axiosArticleSearch = (search: ArticleDto) => {
    return axios.post(Endpoints.ARTICLE + "/search", search, axiosConfig(authData!.token));
  };

  const axiosAuthorSave = (author: AuthorDto) => {
    return axios.post(Endpoints.ARTICLE + "/author/save", author, axiosConfig(authData!.token));
  };

  const axiosGetMyArticle = () => {
    return axios.get(Endpoints.ARTICLE + "/my-list", axiosConfig(authData!.token));
  };

  const axiosGetMyArticleWithStatus = (statusList: any) => {
    return axios.get(`${Endpoints.ARTICLE}/my-list-with-status?status=${statusList}`, axiosConfig(authData!.token));
  };

  const axiosPostDeleteFile = async (articleId: number, fileId: number) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/remove-file/${fileId}`, null, axiosConfig(authData!.token));
  };

  const axiosPostDeleteFiles = (articleId: number, fileId: number[]) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/remove-files`, fileId, axiosConfig(authData!.token));
  };

  const axiosGetArticleById = (id: number) => {
    return axios.get(Endpoints.ARTICLE + "/" + id, axiosConfig(authData!.token));
  };

  const axiosGetBlacklistAuthorList = () => {
    return axios.get(Endpoints.ARTICLE + "/blacklist-author-list", axiosConfig(authData!.token));
  };

  const axiosGetAssistantGetCorrectionMailsList = (id: number) => {
    return axios.get(`${Endpoints.ARTICLE}/${id}/assistant/get-correction-mails`, axiosConfig(authData!.token));
  };

  const axiosGetReviewersStatistics = (params: any) => {
    return axios.get(Endpoints.ARTICLE + "/editor/reviewers-stats", axiosConfig(authData!.token, params));
  };

  const axiosGetAssociateStatistics = () => {
    return axios.get(Endpoints.ARTICLE + "/editor/associates-stats", axiosConfig(authData!.token));
  };

  const axiosGetUploadUserList = () => {
    return axios.get(Endpoints.ARTICLE + "/upload-user-list", axiosConfig(authData!.token));
  };

  const axiosUploadFile = (articleId: number, file: any) => {
    let bodyFormData = new FormData();
    bodyFormData.append("file", file[0]);

    return axios({
      method: "post",
      url: `${Endpoints.ARTICLE}/${articleId}/file-upload`,
      data: bodyFormData,
      headers: { "Content-Type": "multipart/form-data", Authorization: "Bearer " + authData!.token },
    });
  };

  const axiosSetReviewersCount = (articleId: any, count: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/set-reviewers-count?count=${count}`,
      {},
      axiosConfig(authData!.token)
    );
  };

  const axiosGetAddedReviewerList = (articleId: any) => {
    return axios.get(`${Endpoints.ARTICLE}/${articleId}/reviewer/list`, axiosConfig(authData!.token));
  };

  const axiosAddReviewer = (articleId: any, reviewerId: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/reviewer?reviewerId=${reviewerId}`,
      {},
      axiosConfig(authData!.token)
    );
  };

  const axiosDeleteReviewer = (articleId: any, reviewerId: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/reviewer/delete?reviewerId=${reviewerId}`,
      {},
      axiosConfig(authData!.token)
    );
  };

  const axiosGetMailSuggestionToReviewer = (articleId: any, reviewId: any) => {
    return axios.get(
      `${Endpoints.ARTICLE}/${articleId}/review/${reviewId}/mail-suggestion`,
      axiosConfig(authData!.token)
    );
  };

  const axiosGetMailSuggestionToAssociate = (articleId: any) => {
    return axios.get(`${Endpoints.ARTICLE}/${articleId}/editor/get-associate-mails`, axiosConfig(authData!.token));
  };

  const axiosSendMailToAllReviewers = (articleId: any) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/review/send-mail-to-all`, {}, axiosConfig(authData!.token));
  };

  const axiosSendMailToReviewer = (articleId: any, reviewId: any, emailDto: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/review/${reviewId}/send-mail`,
      emailDto,
      axiosConfig(authData!.token)
    );
  };

  const axiosGetAssistantToDoList = () => {
    return axios.get(`${Endpoints.ARTICLE}/assistant/new-articles`, axiosConfig(authData!.token));
  };

  const axiosPostArticleAddNote = (articleId: any, note: string) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/assistant/new-note`,
      {},
      axiosConfig(authData!.token, { note: note })
    );
  };

  const axiosPostArticleRegenerateCombinedPdf = (articleId: any) =>
    axios.get(`${Endpoints.ARTICLE}/${articleId}/assistant/regenerate-combined-pdf`, axiosConfig(authData!.token));

  const axiosPostArticleCorrection = (articleId: any, params: any, body: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/assistant/submit-correction`,
      body,
      axiosConfig(authData!.token, params)
    );
  };

  const axiosPostAddFile = (articleId: any, file: any) =>
    axios.post(`${Endpoints.ARTICLE}/${articleId}/add-file`, file, axiosConfig(authData!.token));

  const axiosPostAssistantDecide = (articleId: any, object: any) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/assistant/decide`, object, axiosConfig(authData!.token));
  };

  const axiosPostSubmitRevision = (articleId: any, object: any) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/submit-revision`, object, axiosConfig(authData!.token));
  };

  const axiosPostSubmitCorrection = (articleId: any, object: any) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/submit-correction`, object, axiosConfig(authData!.token));
  };

  const axiosGetReviewerToDoList = () => {
    return axios.get(`${Endpoints.ARTICLE}/reviewer/todo-list`, axiosConfig(authData!.token));
  };

  const axiosGetAssistantRejectMail = (id: number) => {
    return axios.get(`${Endpoints.ARTICLE}/${id}/assistant/get-reject-mail`, axiosConfig(authData!.token));
  };

  const axiosPostAcceptDeclineArticle = (articleId: any, reviewId: any, response: any) => {
    let obj = { response: response };
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/review-response/${reviewId}`,
      {},
      axiosConfig(authData!.token, obj)
    );
  };

  const axiosPostAssignAssociate = (articleId: any, associateId: any, emailDto: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/editor/assign-associate`,
      emailDto,
      axiosConfig(authData!.token, { associateId: associateId })
    );
  };

  const axiosGetArticleReviewers = (id: number) => {
    return axios.get(`${Endpoints.ARTICLE}/${id}/reviewer/list`, axiosConfig(authData!.token));
  };

  const axiosPostDraftReview = (articleId: any, articleReviewerId: any, review: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/draft-review/${articleReviewerId}`,
      review,
      axiosConfig(authData!.token)
    );
  };

  const axiosPostSubmitReview = (articleId: any, articleReviewerId: any, review: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/submit-review/${articleReviewerId}`,
      review,
      axiosConfig(authData!.token)
    );
  };

  const axiosGetReview = (articleId: any, reviewId: any) => {
    return axios.get(`${Endpoints.ARTICLE}/${articleId}/get-review/${reviewId}`, axiosConfig(authData!.token));
  };

  const axiosGetArticlesForAssociate = () => {
    return axios.get(`${Endpoints.ARTICLE}/associate/article-list`, axiosConfig(authData!.token));
  };

  const axiosGetAuthorList = () => {
    return axios.get(`${Endpoints.ARTICLE}/author/list`, axiosConfig(authData!.token));
  };

  const axiosGetArticleOverallPdf = (id: number) => {
    return axios.get(`${Endpoints.ARTICLE}/${id}/overall`, axiosConfig(authData!.token));
  };

  const axiosGetAssociateMail = (articleId: any, status: any) => {
    return axios.get(
      `${Endpoints.ARTICLE}/${articleId}/associate/get-mail?status=${status}`,
      axiosConfig(authData!.token)
    );
  };

  const axiosPostReturnToAssistant = (articleId: any) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/return-to-assistant`, {}, axiosConfig(authData!.token));
  };

  const axiosPostAssociateMail = (articleId: any, status: any, description: any, comment: string) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/associate/decision`,
      { comment: comment, status: status, description: description },
      axiosConfig(authData!.token)
    );
  };

  const axiosGetEditorMail = (articleId: any, status: any) => {
    return axios.get(
      `${Endpoints.ARTICLE}/${articleId}/editor/get-mail?status=${status}`,
      axiosConfig(authData!.token)
    );
  };

  const axiosPostEditorMail = (articleId: any, status: any, emailDto: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/editor/decision?decision=${status}`,
      emailDto,
      axiosConfig(authData!.token)
    );
  };

  const axiosPostAssignSameReviewers = (articleId: number) => {
    return axios.post(`${Endpoints.ARTICLE}/${articleId}/assign-same-reviewers`, {}, axiosConfig(authData!.token));
  };

  const axiosPostSuggestedReviewer = (articleId: any, suggestedReviewer: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/suggest-new-reviewer`,
      suggestedReviewer,
      axiosConfig(authData!.token)
    );
  };

  const axiosRateReview = (articleId: any, reviewId: any, rating: any) => {
    return axios.post(
      `${Endpoints.ARTICLE}/${articleId}/editor/rate-review/${reviewId}`,
      {},
      axiosConfig(authData!.token, { rate: rating })
    );
  };

  const axiosPostDeleteArticle = (articleId: number) =>
    axios.post(`${Endpoints.ARTICLE}/${articleId}/delete`, null, axiosConfig(authData!.token));

  return {
    axiosGetAllArticles,
    axiosGetTypes,
    axiosArticleSave,
    axiosArticleUpdate,
    axiosGetKeywords,
    axiosAuthorSave,
    axiosGetMyArticle,
    axiosGetMyArticleWithStatus,
    axiosUploadFile,
    axiosGetArticleById,
    axiosGetBlacklistAuthorList,
    axiosGetAssistantGetCorrectionMailsList,
    axiosArticleSearch,
    axiosGetReviewersStatistics,
    axiosGetEditorArticleList,
    axiosSetReviewersCount,
    axiosGetAddedReviewerList,
    axiosAddReviewer,
    axiosDeleteReviewer,
    axiosGetMailSuggestionToReviewer,
    axiosSendMailToAllReviewers,
    axiosSendMailToReviewer,
    axiosGetUploadUserList,
    axiosGetAssistantToDoList,
    axiosPostArticleAddNote,
    axiosPostArticleRegenerateCombinedPdf,
    axiosPostArticleCorrection,
    axiosPostAddFile,
    axiosPostAssistantDecide,
    axiosPostSubmitRevision,
    axiosPostSubmitCorrection,
    axiosGetReviewerToDoList,
    axiosGetAssistantRejectMail,
    axiosPostAcceptDeclineArticle,
    axiosGetArticleReviewers,
    axiosGetMailSuggestionToAssociate,
    axiosPostAssignAssociate,
    axiosPostDraftReview,
    axiosGetReview,
    axiosPostSubmitReview,
    axiosGetArticlesForAssociate,
    axiosGetAssociateMail,
    axiosPostReturnToAssistant,
    axiosPostAssociateMail,
    axiosGetEditorMail,
    axiosPostEditorMail,
    axiosPostAssignSameReviewers,
    axiosGetAssociateStatistics,
    axiosGetAuthorList,
    axiosGetArticleOverallPdf,
    axiosPostSuggestedReviewer,
    axiosRateReview,
    axiosGetAssociateMyArticle,
    axiosGetArticleLog,
    axiosGetArticleEmailLog,
    axiosPostDeleteFile,
    axiosPostDeleteArticle,
    axiosPostDeleteFiles,
  };
}

export type { ArticleControllerType };
